/**
 * Text Mask Reveal Component
 *
 * A reusable scroll-triggered reveal animation using GSAP's SplitText and ScrollTrigger.
 * Splits text into lines, words, or characters and animates them with a mask reveal effect.
 *
 * Usage:
 * Add data-split="heading" to any element you want to animate
 * Add data-split-reveal="lines|words|chars" to specify animation type (default: lines)
 *
 * @example
 * <h1 data-split="heading" data-split-reveal="lines">Your heading text</h1>
 * <h2 data-split="heading" data-split-reveal="words">Another heading</h2>
 * <p data-split="heading" data-split-reveal="chars">Character animation</p>
 *
 * @requires GSAP SplitText plugin (Club GreenSock membership required)
 * @see https://greensock.com/docs/v3/Plugins/SplitText
 */

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";

// Register GSAP plugins
gsap.registerPlugin(SplitText, ScrollTrigger);

function isInViewport(el, threshold = 0.8) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;

  // threshold = 0.8 means "top of element is above 80% of viewport height"
  return rect.top <= vh * threshold;
}

function primeScrollTriggersAndPlayInView(triggers, threshold = 0.8) {
  // Force ST to calculate positions and immediately evaluate
  ScrollTrigger.refresh(true);
  ScrollTrigger.update(true);

  triggers.forEach(({ triggerEl, animation }) => {
    if (!animation || animation.progress() > 0) return; // already played/playing

    if (isInViewport(triggerEl, threshold)) {
      animation.play(0);
      // If you used "once", also kill the trigger to avoid extra work
      animation.scrollTrigger?.kill();
    }
  });
}

/**
 * Configuration for different split types
 * Adjust duration and stagger to your preference
 */
const splitConfig = {
  lines: { duration: 0.8, stagger: 0.08 },
  words: { duration: 0.6, stagger: 0.06 },
  chars: { duration: 0.4, stagger: 0.01 },
};

/**
 * Initialize text mask reveal animations
 * Finds all elements with [data-split="heading"] and animates them based on their split-reveal type
 *
 * @param {Object} options - Configuration options
 * @param {string} options.selector - CSS selector for elements to animate (default: '[data-split="heading"]')
 * @param {string} options.triggerStart - ScrollTrigger start position (default: 'clamp(top 80%)')
 * @param {string} options.ease - GSAP easing function (default: 'expo.out')
 * @param {boolean} options.once - Only animate once (default: true)
 * @returns {Array} Array of created animations
 */
export function initTextMaskReveal(options = {}) {
  const defaultOptions = {
    selector: '[data-split="heading"]',
    triggerStart: "clamp(top 80%)",
    ease: "expo.out",
    once: true,
    ...options,
  };

  const animations = [];

  document.querySelectorAll(defaultOptions.selector).forEach((heading) => {
    // Reveal the element (hidden in CSS to prevent FOUC)
    gsap.set(heading, { autoAlpha: 1 });

    // Get the split type from data attribute, default to 'lines'
    const type = heading.dataset.splitReveal || "lines";

    // Determine which types to split based on the desired animation
    // lines → split by lines only
    // words → split by lines and words
    // chars → split by lines, words and characters
    const typesToSplit =
      type === "lines"
        ? ["lines"]
        : type === "words"
          ? ["lines", "words"]
          : ["lines", "words", "chars"];

    // Split the text (without creating animation in callback)
    const splitInstance = SplitText.create(heading, {
      type: typesToSplit.join(", "),
      mask: "lines", // Wrap each line in an overflow:hidden div
      linesClass: "line",
      wordsClass: "word",
      charsClass: "letter",
    });

    // Get the animation targets based on split type
    const targets = splitInstance[type];

    // Get the matching config for this split type
    const config = splitConfig[type];

    // Set initial state FIRST (elements hidden below mask)
    gsap.set(targets, { yPercent: 110 });

    // Create the animation AFTER split is complete
    // Use gsap.to() instead of gsap.from() for more reliable ScrollTrigger behavior
    const animation = gsap.to(targets, {
      yPercent: 0,
      duration: config.duration,
      stagger: config.stagger,
      ease: defaultOptions.ease,
      scrollTrigger: {
        trigger: heading,
        start: defaultOptions.triggerStart,
        once: defaultOptions.once,
        toggleActions: "play none none none",
        // Important: Check if element is already in view on page load
        immediateRender: false,
      },
    });

    animations.push(animation);
  });

  // Refresh ScrollTrigger after all splits and animations are created
  requestAnimationFrame(() => {
    ScrollTrigger.refresh();

    // Force ScrollTrigger to update and check all triggers
    // This is critical for elements already in view at page load (scroll position 0)
    ScrollTrigger.update();
  });

  return animations;
}

/**
 * Create a single text mask reveal animation
 * Useful for dynamically added content
 *
 * @param {HTMLElement} element - The element to animate
 * @param {Object} options - Animation options
 * @param {string} options.type - Split type: 'lines', 'words', or 'chars' (default: 'lines')
 * @param {string} options.triggerStart - ScrollTrigger start position (default: 'clamp(top 80%)')
 * @param {string} options.ease - GSAP easing function (default: 'expo.out')
 * @param {boolean} options.once - Only animate once (default: true)
 * @returns {Object} The GSAP animation
 *
 * @example
 * const heading = document.querySelector('.dynamic-heading');
 * createTextMaskReveal(heading, { type: 'words' });
 */
export function createTextMaskReveal(element, options = {}) {
  const defaultOptions = {
    type: "lines",
    triggerStart: "clamp(top 80%)",
    ease: "expo.out",
    once: true,
    ...options,
  };

  // Reveal the element
  gsap.set(element, { autoAlpha: 1 });

  const type = defaultOptions.type;
  const typesToSplit =
    type === "lines"
      ? ["lines"]
      : type === "words"
        ? ["lines", "words"]
        : ["lines", "words", "chars"];

  // Split the text
  const splitInstance = SplitText.create(element, {
    type: typesToSplit.join(", "),
    mask: "lines",
    linesClass: "line",
    wordsClass: "word",
    charsClass: "letter",
  });

  // Get the animation targets and config
  const targets = splitInstance[type];
  const config = splitConfig[type];

  // Set initial state FIRST (elements hidden below mask)
  gsap.set(targets, { yPercent: 110 });

  // Create the animation AFTER split is complete
  // Use gsap.to() instead of gsap.from() for more reliable ScrollTrigger behavior
  const animation = gsap.to(targets, {
    yPercent: 0,
    duration: config.duration,
    stagger: config.stagger,
    ease: defaultOptions.ease,
    scrollTrigger: {
      trigger: element,
      start: defaultOptions.triggerStart,
      once: defaultOptions.once,
      toggleActions: "play none none none",
      immediateRender: false,
    },
  });

  // Refresh ScrollTrigger to recalculate positions
  requestAnimationFrame(() => {
    ScrollTrigger.refresh();
    ScrollTrigger.update();
  });

  return { splitInstance, animation };
}

/**
 * Initialize text mask reveal for CMS-rendered HTML containers
 * Handles cases where HTML comes from CMS with raw output
 *
 * This function looks for containers with [data-text-reveal-container]
 * and animates the first text element inside (h1, h2, h3, h4, h5, h6, p)
 *
 * @param {Object} options - Configuration options
 * @param {string} options.containerSelector - CSS selector for container elements (default: '[data-text-reveal-container]')
 * @param {string} options.triggerStart - ScrollTrigger start position (default: 'clamp(top 80%)')
 * @param {string} options.ease - GSAP easing function (default: 'expo.out')
 * @param {boolean} options.once - Only animate once (default: true)
 * @returns {Array} Array of created animations
 *
 * @example
 * <div data-text-reveal-container data-reveal-type="lines">
 *   {{ entry.heading | raw }}
 * </div>
 */
export function initTextMaskRevealFromContainers(options = {}) {
  const defaultOptions = {
    containerSelector: "[data-text-reveal-container]",
    triggerStart: "top 80%",
    ease: "expo.out",
    once: true,
    ...options,
  };

  const animations = [];
  const created = []; // keep references for “already in view” priming

  document
    .querySelectorAll(defaultOptions.containerSelector)
    .forEach((container) => {
      // Get the animation type from the container's data attribute
      const type = container.dataset.revealType || "lines";

      // Find the first heading or paragraph element inside
      const textElement = container.querySelector("h1, h2, h3, h4, h5, h6, p");

      if (!textElement) {
        console.warn(
          "textMaskReveal: No text element found in container",
          container,
        );
        return;
      }

      // Reveal the element
      gsap.set(textElement, { autoAlpha: 1 });

      // Determine which types to split
      const typesToSplit =
        type === "lines"
          ? ["lines"]
          : type === "words"
            ? ["lines", "words"]
            : ["lines", "words", "chars"];

      // Split the text
      const splitInstance = SplitText.create(textElement, {
        type: typesToSplit.join(", "),
        mask: "lines",
        linesClass: "line",
        wordsClass: "word",
        charsClass: "letter",
      });

      // Get the animation targets and config
      const targets = splitInstance[type];
      const config = splitConfig[type];

      // Set initial state FIRST (elements hidden below mask)
      gsap.set(targets, { yPercent: 110 });

      // ✅ build animation paused
      const animation = gsap.to(targets, {
        yPercent: 0,
        duration: config.duration,
        stagger: config.stagger,
        ease: defaultOptions.ease,
        paused: true,
      });

      // ✅ create ScrollTrigger separately
      const st = ScrollTrigger.create({
        trigger: textElement,
        start: defaultOptions.triggerStart,
        once: defaultOptions.once,
        onEnter: () => animation.play(0),
        onEnterBack: () => animation.play(0),
      });

      // attach so prime helper can kill it if needed
      animation.scrollTrigger = st;

      animations.push(animation);
      created.push({ triggerEl: textElement, animation });
    });

  // ✅ after initial layout pass
  requestAnimationFrame(() => {
    primeScrollTriggersAndPlayInView(created, 0.8);
  });

  // ✅ after full load (covers late image/font/layout shifts)
  window.addEventListener(
    "load",
    () => {
      primeScrollTriggersAndPlayInView(created, 0.8);
    },
    { once: true },
  );

  return animations;
}

/**
 * Wait for fonts to load before initializing
 * This prevents layout shifts and ensures accurate text splitting
 *
 * @param {Object} options - Configuration options (same as initTextMaskReveal)
 * @returns {Promise} Promise that resolves when animations are initialized
 */
export function initTextMaskRevealWhenFontsReady(options = {}) {
  return document.fonts.ready.then(() => {
    // Initialize both standard elements and CMS containers
    const standardAnimations = initTextMaskReveal(options);
    const containerAnimations = initTextMaskRevealFromContainers(options);
    return [...standardAnimations, ...containerAnimations];
  });
}

// Export as object for cleaner imports
export const textMaskReveal = {
  init: initTextMaskReveal,
  initWhenFontsReady: initTextMaskRevealWhenFontsReady,
  initFromContainers: initTextMaskRevealFromContainers,
  create: createTextMaskReveal,
};
